import { useEffect, useState, type CSSProperties } from 'react';
import { Icon } from '../../icons/registry';
import { useIconSystem } from '../../../hooks/useIconSystem';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../contexts/AuthContext';
import { isNativeApp } from '../../../lib/platform';
// B26 — save-failure copy lives in its own runtime-independent module
// (mirroring lib/oauthChipCopy.ts); the SESSION_LOST_* strings are PROVISIONAL
// and awaiting founder ratification.
import {
  SAVE_RETRY_MESSAGE,
  SESSION_LOST_ACTION_LABEL,
  SESSION_LOST_MESSAGE,
  SESSION_LOST_MESSAGE_V2,
} from '../../../lib/onboardingSaveCopy';
import { COACHES, LEAD_COACH } from '../../../fixtures/coaches';
import {
  WELCOME_FIRST_MESSAGES,
  type JourneyId,
} from '../../../fixtures/onboarding';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import {
  checkOnboardingName,
  usePersistOnboarding,
} from '../../../hooks/usePersistOnboarding';
import { parqAnswersComplete } from '../../../lib/parqAnswers';
// B118 — the client half of the refusal signal (see the call site below).
import { track } from '../../../lib/analytics';
import { useServerFlag, useServerFlagReady } from '../../../hooks/useServerFlag';
import { getServerFlags, retryConfigRead } from '../../../lib/supabase';
import { renderCoachName } from '../../../lib/coachName';
import { deriveDisplayName } from '../../../lib/displayName';
import { TayroMark } from '../../brand/TayroMark';
import styles from './Step7Welcome.module.css';
import { Step7WelcomeV2 } from './Step7Welcome.v2';

type Step7Props = {
  answers: OnboardingAnswers;
  /**
   * O3 — "Change my answers": re-enter the wizard from the reveal.
   *
   * ⚠️ BUILD-CRITICAL (CD handoff 2026-08-30, founder addition). This is the
   * ONLY backward affordance on the reveal, and it must run WITHOUT the atomic
   * completion POST. `persistOnboardingComplete` rides the dashboard CTA and
   * nothing else, so re-entering here leaves onboarding genuinely incomplete —
   * exactly what an athlete who wants to change an answer expects.
   *
   * The route owns where re-entry lands (and therefore the recomputed B45 CTA
   * label). Optional so the reveal degrades to a CTA-only screen rather than a
   * dead control if a caller has not wired re-entry yet.
   */
  onChangeAnswers?: () => void;
  /**
   * B108 — where the athlete goes when the dashboard CTA is pressed on an
   * INCOMPLETE screening bag.
   *
   * ⚠️ BUILD-CRITICAL. `buildOnboardingPayload` stops stamping the five
   * completion keys when the PAR-Q bag is short of seven explicit booleans, so
   * this screen can be reached (a legacy blob, a manufactured `?step=7`, a
   * flow interrupted mid-screening) in a state where "Go to Today"
   * would persist nothing that admits anyone. Rather than POST a half payload
   * and navigate to a Today that will bounce them, the CTA sends the athlete
   * to the screening question. No new copy: the wizard's own Step 3 asks it.
   *
   * Optional only because `onChangeAnswers` is: with no handler the CTA
   * refuses to persist and stays where it is, which is the fail-closed
   * outcome. The route wires it, and the route test pins that it does.
   */
  onScreeningIncomplete?: () => void;
  /**
   * Round-31 UX D2 — open on the settled frame instead of staging.
   *
   * The B108 bounce sends an athlete to the screening and her Continue brings
   * her straight back here. Remounting replayed the full 3.5s sequence, so she
   * watched a machine claim to read answers it read ninety seconds earlier,
   * with no control on screen for the first 2.5s of it. The route sets this
   * only on that return leg (its `bouncedFromReveal` flag is retired anywhere
   * but steps 3 and 7), so a first arrival still stages exactly as ratified.
   *
   * Same mechanism as reduced motion below: the sequence is a pure timer, so
   * "settled" is simply its end state, reached without the wait.
   */
  settled?: boolean;
};

/**
 * O3 — the ratified pre-reveal thinking sequence (CD handoff 2026-08-30).
 *
 * ⚠️ SP-10 HONESTY CONSTRAINT — these three lines describe CONTEXT BUILDING and
 * COACH BRIEFING. They never claim a plan is being written, because none is:
 * the sequence is a pure timer (see the effect below) and this component's only
 * network call is the completion POST on the dashboard CTA. Any future edit
 * that makes one of these lines describe plan generation, or that hangs real
 * work off the timers, breaks the constraint the founder ratified — and the
 * SP-10 tests in `Step7Welcome.test.tsx` fail loudly if it does.
 */
export const THINKING_SEQUENCE_LINES = [
  'Reading your answers',
  'Learning what you are training for',
  'Briefing your five coaches',
] as const;

/**
 * When each row settles to done, and when the reveal takes over — the exact
 * cadence of the ratified simulation (~1s a row, ~3.5s total).
 */
export const THINKING_SEQUENCE_MS = {
  row1Done: 1000,
  row2Done: 2000,
  row3Done: 2900,
  reveal: 3500,
} as const;

/** Row state for the staged sequence. Drives `data-state` (and the tests). */
type ThinkingRowState = 'done' | 'active' | 'pending';

/**
 * Step 7 — the coaches reveal (chapter 4 finale).
 *
 * Renders inside OnboardingShell's bare layout (chapter 4 ambient #EBE4DB) and
 * owns its own chrome, because the reveal is an outcome screen rather than a
 * question: it carries the Tayro mark plus a full 4/4 chapter segment bar, and
 * it deliberately has NO back chevron (the shell's bare variant renders none,
 * and this component adds none — "Change my answers" is the only way back).
 *
 * Two phases:
 *
 *   1) THINKING (~3.5s) — serif "Setting up your coaching team…" over three
 *      staged rows: done = circle-check, active = spinner, pending = dot.
 *      SP-10: this is a pure timer. It creates no plan data, requests nothing,
 *      and writes nothing; the first plan is drafted later, elsewhere.
 *   2) REVEAL — two columns on desktop (stacked on mobile): the personalized
 *      serif headline, the dark first-message card from AIro, the dashboard CTA
 *      with "Change my answers" beside it, and the five coach cards staggering
 *      in on the right.
 *
 * **Phase 7 PR 1 — Server persistence on CTA**:
 *
 * Pre-this-PR, the CTA was a `<Link to="/today">` that navigated
 * client-side with NO server write. Onboarding answers stayed in
 * localStorage only. After Phase 7 PR 2 retires vanilla, that would
 * leave first-time athletes with no `_onboarding_completed_at` flag —
 * the Today route's gate would loop them back to onboarding forever.
 *
 * Now: clicking the CTA fires `persistOnboardingComplete(answers)`
 * which atomically POSTs `_journey`, `_journey_set_at`,
 * `_parq_completed{,_at}`, and `_onboarding_completed{,_at}` to
 * `/api/context/:userId` in ONE request. On success → navigate to
 * `/today` (the React Router basename="/v2" makes the browser URL
 * `/v2/today`). On error → inline error chip + CTA stays enabled
 * for retry.
 *
 * The journey-specific first message is sourced from
 * WELCOME_FIRST_MESSAGES per the user's answers.journey. Lead coach
 * (AIro / training-coach) speaks it.
 */
export function Step7Welcome({
  answers,
  onChangeAnswers,
  onScreeningIncomplete,
  settled,
}: Step7Props) {
  const iconsOn = useIconSystem();
  // SP-3 — when first_plan_v1 is ON, the CTA hands off to /v2/plan/first-look
  // (which runs the REAL generation with honest progress), so the pre-reveal
  // sequence is skipped. Flag OFF → sequence → reveal → /today.
  // ⚖️ B149 fix-2 — and `ready` with it. The CTA's navigate is a ONE-SHOT
  // decision (see `handleComplete`), so it may not be taken on a value nobody
  // has said yet; `skipSequence` below is not one-shot and keeps reading the
  // value alone, exactly as before.
  const firstPlanFlag = useServerFlagReady('first_plan_v1');
  const firstPlanOn = firstPlanFlag.enabled;
  // Tayro 2.0 Part 7 — the onboarding restyle. Read as a literal, with every
  // other hook, above the one branch below. Flag off returns the tree below it
  // byte for byte; flag on hands the same state to `Step7Welcome.v2.tsx`.
  const restyle = useServerFlag('onboarding_2_0_v1');
  const reducedMotion = useReducedMotion();
  // Reduced motion settles the sequence instead of staging it: no spinner, no
  // staged delay, the reveal is simply already there. Round-31 UX D2 — the
  // return from the B108 bounce takes that same path: she has already watched
  // the staging, so `settled` opens the reveal on the frame she left.
  const skipSequence = firstPlanOn || reducedMotion || settled === true;

  const [phase, setPhase] = useState<'thinking' | 'reveal'>(() =>
    skipSequence ? 'reveal' : 'thinking',
  );
  /** How many rows have settled to done. `LINES.length` = all done. */
  const [rowsDone, setRowsDone] = useState<number>(() =>
    skipSequence ? THINKING_SEQUENCE_LINES.length : 0,
  );
  // Phase 7 PR 1 — CTA save state machine.
  // 'idle'         → ready to click
  // 'saving'       → POST in flight, CTA disabled, label = "Setting up…"
  // 'error'        → save failed transiently (network / server), CTA
  //                  re-enabled, retry chip visible — a retry CAN succeed
  // 'session_lost' → B26: the session is gone, so the CTA is retired (a
  //                  retry is structurally impossible) and the chip offers
  //                  the one action that works: sign in again
  const [ctaSaveState, setCtaState] = useState<
    'idle' | 'saving' | 'error' | 'session_lost'
  >('idle');
  /**
   * ⚖️ B149 fix-2 — WHILE THE FLAG IS UNREAD THE CTA IS BUSY, NOT LIVE.
   *
   * The tap below takes a ONE-SHOT route (`/plan/first-look` or `/today`) off
   * `first_plan_v1`, and `/api/config` answers 503 with no flags map at all
   * while the instance serving it has never completed a `feature_flags` read.
   * `enabled` is false in that window because nobody has said otherwise yet,
   * not because the flag is off, so the athlete would be routed past a first
   * look her account is entitled to, once, with no way back.
   *
   * So the wait wears the frame this surface already has for "not yet": the
   * 'saving' state, disabled and `aria-busy` with the label it already
   * carries. No new copy, no new spinner, and the double-click guard and the
   * `disabled` expression below keep the words they were reviewed with.
   *
   * ⚖️ B149 fix-4 (F5) — THE BOUND, CORRECTED. Fix-3 replaced "four reads,
   * about six seconds" with a wall-clock ceiling: 4 reads × 8 s + 3 waits ×
   * the body's `retryAfterSeconds` is about 38 s with the 2 s this deployment
   * sends (`CONFIG_READ_TIMEOUT_MS` in `lib/supabase.ts`), and B149 fix-2's own
   * A1 guard makes "it always settles" exception-safe regardless. In a real
   * wizard the flags settled minutes before this screen is reached.
   */
  const ctaState =
    ctaSaveState === 'idle' && !firstPlanFlag.ready ? 'saving' : ctaSaveState;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const auth = useAuthContext();
  const { persistOnboardingComplete } = usePersistOnboarding();

  // ── The thinking sequence — SP-10 PURE TIMERS ──────────────────────────
  // Nothing in this effect reads, writes, fetches, generates or persists
  // anything. It moves a counter on a clock and then swaps the phase. The
  // ratified lines are honest precisely because this is all that happens.
  useEffect(() => {
    if (skipSequence) {
      setRowsDone(THINKING_SEQUENCE_LINES.length);
      setPhase('reveal');
      return;
    }
    const timers = [
      setTimeout(() => setRowsDone(1), THINKING_SEQUENCE_MS.row1Done),
      setTimeout(() => setRowsDone(2), THINKING_SEQUENCE_MS.row2Done),
      setTimeout(() => setRowsDone(3), THINKING_SEQUENCE_MS.row3Done),
      setTimeout(() => setPhase('reveal'), THINKING_SEQUENCE_MS.reveal),
    ];
    return () => timers.forEach((t) => clearTimeout(t));
  }, [skipSequence]);

  const journey: JourneyId = answers.journey ?? 'health_fitness';
  const firstMessage = WELCOME_FIRST_MESSAGES[journey];

  // B73 reuse — the reveal addresses the athlete through the SAME chain the
  // Today greeting uses (`lib/displayName`), fed the name the wizard just
  // collected. One fallback order in the app; never an email fragment; a blank
  // answer simply lands on the nameless headline.
  const athleteName = deriveDisplayName(auth.user, wizardNameContext(answers));
  const headlineLead = athleteName ? `${athleteName}, your` : 'Your';

  const handleComplete = async () => {
    if (ctaState === 'saving' || ctaState === 'session_lost') return; // double-click guard
    // ⚖️ B149 fix-2 — THE ONE-SHOT DECISION IS NEVER TAKEN ON AN UNREAD FLAG.
    // The busy CTA above is what the athlete sees during the wait (and the
    // guard on the line above already refuses the tap while it says 'saving');
    // this line is what makes the branch unreachable from every other entry —
    // a retry after a failed save, or any programmatic call — because a
    // navigate taken once cannot be re-taken.
    if (!firstPlanFlag.ready) return;
    // ── B108 — no completion without the screening behind it ───────────────
    // The reveal is reachable without Step 3 having run (a legacy localStorage
    // blob, an interrupted flow), and the payload builder now refuses to claim
    // a screening it cannot show. Firing the POST anyway would write a partial
    // patch and then navigate to a Today that has no completion marker to
    // read. So the CTA does the only honest thing: it asks the question.
    // Nothing is persisted on this branch, and nothing is inferred.
    //
    // B118 (round-18 critic 3) — this branch used to leave no trace that it
    // fired. `track` is a no-op in production today (`lib/analytics.ts`
    // installs no provider), but it costs nothing to call and is the client
    // half of the same durable signal the server writes on its own refusal.
    if (!parqAnswersComplete(answers.parq)) {
      track('onboarding:completion_refused');
      onScreeningIncomplete?.();
      return;
    }
    // ⚖️ B149 fix-4 (F2) — A GIVE-UP IS NOT AN ANSWER. `ready` above settles on
    // the empty map exactly like it settles on a real 200 — PROBE F: a boot
    // that gave up on `{}`, this screen reached minutes later against a now-
    // warm instance. So the one-way navigate below is not taken on a value
    // nobody has actually said: one bounded re-ask, through `retryConfigRead`'s
    // one door, wearing the same 'saving' frame the unread-flag wait already
    // wears. A successful read applies the map as authoritative and this reads
    // it fresh; a failed one decides on the non-authoritative value already in
    // hand (`firstPlanOn`) — never a second re-ask.
    let firstPlanOnAtDecision = firstPlanOn;
    if (!firstPlanFlag.authoritative) {
      setCtaState('saving');
      try {
        await retryConfigRead();
        firstPlanOnAtDecision = Boolean(getServerFlags().first_plan_v1);
      } catch {
        /* the re-ask itself failed — decide on the value already in hand */
      }
    }
    setCtaState('saving');
    setErrorMessage(null);
    try {
      await persistOnboardingComplete(answers);
      // React Router uses basename="/v2"; passing '/today' resolves to
      // the browser URL `/v2/today`. NEVER pass '/v2/today' here — that
      // would double-prefix to `/v2/v2/today` and 404.
      // SP-3 — flag ON hands off to the first-plan review flow; OFF → /today.
      navigate(firstPlanOnAtDecision ? '/plan/first-look' : '/today');
    } catch (err) {
      // B26 — tell the truth about WHICH failure this is. The discriminator is
      // the session itself, not the thrown message: if there is no user at the
      // moment of failure, no number of retries can save anything (the persist
      // needs a user id for the endpoint and a JWT for the call), so offering
      // "Try again" would send the athlete in circles. Read from auth rather
      // than string-matching the "Not signed in" error, so the branch stays
      // right whichever layer refuses first.
      const sessionLost = !auth.user;
      // B437 / B438 to B444 — a refusal the SERVER made on a field's value
      // (`400 { error, code: 'invalid_field', field }`) is not a transient
      // failure: the same value goes up on every press, so the generic retry
      // line is a promise that cannot come true. The server's own sentence is
      // shown instead (the persist hook keeps it only when it is a plain
      // sentence of 200 characters or fewer). Rendered as text, never markup.
      const refusal =
        err && typeof err === 'object' && (err as { code?: unknown }).code === 'invalid_field'
          ? (err as { userMessage?: unknown }).userMessage
          : undefined;
      setCtaState(sessionLost ? 'session_lost' : 'error');
      setErrorMessage(
        sessionLost
          ? restyle
            ? SESSION_LOST_MESSAGE_V2
            : SESSION_LOST_MESSAGE
          : typeof refusal === 'string' && refusal !== ''
            ? refusal
            : SAVE_RETRY_MESSAGE,
      );
      // eslint-disable-next-line no-console
      console.warn('[Step7Welcome] persistOnboardingComplete failed:', err);
    }
  };

  // Part 7 — B465: Sign out has left the top row for the account button, and
  // the reveal, which had no Sign out, gets one. Same exit the wizard's shell
  // takes: sign out, then the landing.
  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch {
      /* Supabase clears local state regardless */
    }
    navigate('/landing');
  };

  if (restyle) {
    return (
      <Step7WelcomeV2
        phase={phase}
        lines={THINKING_SEQUENCE_LINES}
        rowsDone={rowsDone}
        headlineLead={headlineLead}
        accountName={athleteName}
        signedIn={Boolean(auth.user)}
        onSignOut={() => void handleSignOut()}
        firstMessage={firstMessage}
        ctaState={ctaState}
        buttonLabel={ctaState === 'saving' ? 'Setting up…' : 'Go to Today'}
        errorMessage={errorMessage}
        sessionLostAction={
          ctaState === 'session_lost'
            ? { label: SESSION_LOST_ACTION_LABEL, to: isNativeApp() ? '/welcome' : '/landing' }
            : null
        }
        onComplete={() => void handleComplete()}
        onChangeAnswers={onChangeAnswers}
        iconsOn={iconsOn}
      />
    );
  }

  return (
    <div className={styles.welcome}>
      {/* Chapter chrome — the reveal keeps the wizard's identity: mark on the
          left, a full 4/4 segment bar on the right. No back chevron here (the
          bare shell renders none, and the reveal adds none). */}
      <header className={styles.chapterChrome}>
        <div className={styles.brand}>
          <TayroMark size={22} />
          <span>Tayro</span>
        </div>
        <div className={styles.chapterProgress}>
          <div className={styles.segments} aria-hidden>
            <span className={styles.segment} />
            <span className={styles.segment} />
            <span className={styles.segment} />
            <span className={styles.segment} />
          </div>
          <div className={styles.chapterLabel}>
            Chapter 4 of 4 · Meeting your coaches
          </div>
        </div>
      </header>

      <div className={styles.stage}>
        {phase === 'thinking' ? (
          <div className={styles.thinking} role="status" aria-live="polite">
            <div className={styles.thinkingTitle}>
              Setting up your coaching team…
            </div>
            <ol className={styles.thinkingRows}>
              {THINKING_SEQUENCE_LINES.map((line, i) => {
                const state: ThinkingRowState =
                  i < rowsDone ? 'done' : i === rowsDone ? 'active' : 'pending';
                return (
                  <li
                    key={line}
                    className={styles.thinkingRow}
                    data-thinking-row={line}
                    data-state={state}
                  >
                    <span className={styles.thinkingGlyph} aria-hidden>
                      {state === 'done' ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="8" fill="var(--signal)" />
                          <path
                            d="M4.5 8.5L7 11l4.5-6"
                            stroke="var(--paper)"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : state === 'active' ? (
                        <span className={styles.thinkingSpinner} />
                      ) : (
                        <span className={styles.thinkingDot} />
                      )}
                    </span>
                    <span className={styles.thinkingLabel}>{line}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          <div className={styles.reveal}>
            <div className={styles.revealMain}>
              <div className={styles.kicker}>You&rsquo;re in</div>
              <h2 className={styles.title}>
                {headlineLead} coaching team is{' '}
                <span className={styles.titleEm}>ready</span>.
              </h2>
              <p className={styles.sub}>
                Five AI coaches, each specializing in one domain. Call any one
                of them, any time, through natural conversation.
              </p>

              <div className={styles.firstMessageCard}>
                <div className={styles.firstMessageHeader}>
                  <span className={styles.firstMessageDot} aria-hidden />
                  <span className={styles.firstMessageKicker}>
                    First message ·{' '}
                    {renderCoachName(LEAD_COACH.name, 'var(--today)')} ·{' '}
                    {LEAD_COACH.role}
                  </span>
                </div>
                <div className={styles.firstMessageBody}>{firstMessage}</div>
              </div>

              <div className={styles.ctaRow}>
                {/* Phase 7 PR 1 — CTA changed from <Link> to <button> so the
                    click handler can fire persistOnboardingComplete + await
                    before navigating. Disabled state during 'saving' prevents
                    double-click while the POST is in flight.

                    B26 — also disabled in 'session_lost': a control that cannot
                    succeed must not stay clickable. The chip carries the action.

                    ⚖️ B149 fix-2 — and the same rule one step earlier, through
                    `ctaState` itself: while `first_plan_v1` is still unread the
                    CTA presents as 'saving', so a control that cannot yet
                    decide WHERE it goes is not clickable either. */}
                <button
                  type="button"
                  className={styles.cta}
                  onClick={() => void handleComplete()}
                  disabled={ctaState === 'saving' || ctaState === 'session_lost'}
                  aria-busy={ctaState === 'saving'}
                >
                  {/* ⚖️ B107 fix-4 (harvest-27, carried from fix-3) — "Today"
                      is what this product calls the place this walk ends: the
                      tab's own label, and the word the first-look flow's own
                      way out now uses. "my dashboard" was this button's word
                      alone. */}
                  {ctaState === 'saving' ? 'Setting up…' : 'Go to Today'}
                  {/* A2 — `sys.arrow` @14. Its own pick: an arrow reads as
                      "go there", a chevron as "there is more". */}
                  {iconsOn ? (
                    <Icon name="sys.arrow" size={14} />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                      <path
                        d="M3 7h8m-3-3l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>

                {/* O3 — the quiet way back. ⚠️ It calls `onChangeAnswers` and
                    NOTHING else: no persist, no navigate, no completion flags.
                    Onboarding stays incomplete until the dashboard CTA says
                    otherwise. The CTA label the athlete meets on re-entry is
                    recomputed by the route (B45) — this component caches no
                    label, which is why re-entry can never re-announce the
                    reveal early. */}
                {onChangeAnswers ? (
                  <button
                    type="button"
                    className={styles.changeAnswers}
                    onClick={onChangeAnswers}
                  >
                    Change my answers
                  </button>
                ) : null}
              </div>

              {/* Phase 7 PR 1 — Inline error chip below the CTA. Persists
                  until the next click (which clears `errorMessage` at the
                  top of `handleComplete`). Uses role="alert" so screen
                  readers announce it.

                  B26 — the session-lost variant carries the recovery action with
                  it. The chip is the only place left that can offer one: the CTA
                  above is disabled in this state precisely because clicking it
                  again cannot work. Destination matches the route's auth gate
                  (native signs in on /welcome, web on /landing), and the answers
                  really are safe — the wizard blob is stamped with this athlete's
                  own user id, so signing back in returns the same id and the Today
                  gate migrates it (see loadPersistedStateForUser). */}
              {(ctaState === 'error' || ctaState === 'session_lost') && errorMessage ? (
                <div className={styles.errorChip} role="alert" aria-live="assertive">
                  <span>
                    {errorMessage}
                    {ctaState === 'session_lost' ? (
                      <>
                        {' '}
                        <Link
                          to={isNativeApp() ? '/welcome' : '/landing'}
                          className={styles.errorChipAction}
                        >
                          {SESSION_LOST_ACTION_LABEL}
                        </Link>
                      </>
                    ) : null}
                  </span>
                </div>
              ) : null}
            </div>

            <div className={styles.coachColumn}>
              <div className={styles.coachColumnLabel}>Your five coaches</div>
              {COACHES.map((c, i) => (
                <div
                  key={c.id}
                  className={styles.coachCard}
                  style={
                    {
                      '--coach-color': c.token,
                      '--coach-index': String(i),
                    } as CSSProperties & Record<string, string>
                  }
                >
                  <span className={styles.coachRule} aria-hidden />
                  <span className={styles.coachAvatar} aria-hidden>
                    {c.initial}
                  </span>
                  <span className={styles.coachBody}>
                    <span className={styles.coachName}>
                      {renderCoachName(c.name, c.token)}
                    </span>
                    <span className={styles.coachDesc}>{c.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Project the wizard's own name answer onto the context key `lib/displayName`
 * reads, so the reveal resolves a name through the ONE shared chain
 * (`preferred_name` → `Name` → sign-in metadata → nothing) rather than a second
 * copy of it. `answers.displayName` is the "what should we call you" answer and
 * maps onto `context_data.preferred_name`, which is where it persists.
 *
 * A skip (`''`, which O2 writes deliberately) simply falls through the chain —
 * so the reveal addresses the athlete with exactly the name Today's greeting
 * will use a moment later, including "no name at all". Never an email fragment,
 * and never a guess.
 *
 * B438 — and a name outside the item 12 rule (a stale blob, a resumed answer)
 * falls through too: the payload does not save it, so the headline must not
 * print a name the save would refuse.
 */
function wizardNameContext(answers: OnboardingAnswers): Record<string, unknown> {
  const name = checkOnboardingName(answers.displayName);
  return { preferred_name: name.ok ? name.value : '' };
}

/**
 * `useReducedMotion()` — true when the athlete asked for reduced motion. Mirrors
 * the GeneratingOverlay helper: matchMedia with a listener, SSR-safe, so a
 * settings change mid-flow takes effect.
 */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  return reduced;
}
